import { createSelector } from '@reduxjs/toolkit';
import { orderBy } from 'lodash-es';
import { lruMemoize } from 'reselect';

import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';
import type { AssetsSegmentSchemaWithIdPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import type { AssetsSegmentSchema } from '@iconik-io/iconik-api/src/services/assets';

import { MENTION_REGEX } from '../../../../utils/constants/mentions';
import type { SortOrder } from '../../../../utils/sort/types';
import type { CommentSortExpression } from './use_comment_entity_list';

type IdsMap = {
  [id: string]: string[];
};

function collectChildrenIds(
  id: string,
  childrenMap: IdsMap,
  visited: Set<string>,
  childrenRecursively: string[],
): void {
  for (const childId of childrenMap[id] ?? []) {
    // Guards against a parent_id cycle in the data, which would recurse forever.
    if (visited.has(childId)) {
      continue;
    }
    visited.add(childId);
    childrenRecursively.push(childId);
    collectChildrenIds(childId, childrenMap, visited, childrenRecursively);
  }
}

/**
 * Recursively gets flat IDs array of all children of a comment.
 */
function getChildrenIds(id: string, childrenMap: IdsMap): string[] {
  const childrenRecursively: string[] = [];
  collectChildrenIds(id, childrenMap, new Set([id]), childrenRecursively);

  return childrenRecursively;
}

const getChildrenMap = lruMemoize(
  (comments: AssetsSegmentSchemaWithIdPatched[]) => {
    const rawChildrenMap: IdsMap = {};
    const byId = new Map<string, AssetsSegmentSchemaWithIdPatched>(
      comments.map((c) => [c.id, c]),
    );

    // Comments can be nested deeply, but we don't currently reflect that in the UI.
    // Instead, we show all replies as direct children of the root comment.
    // However, we still need to consider the full tree structure when filtering out deleted comments,
    // so that if a parent comment is deleted but has non-deleted children, it is retained in the list.

    // First pass: build full parent -> children map (include deleted for evaluation)
    for (const { id, parent_id } of comments) {
      if (!parent_id) {
        continue;
      }
      rawChildrenMap[parent_id] ??= [];
      rawChildrenMap[parent_id].push(id);
    }

    // Cache for subtree evaluation to avoid recomputation
    const subtreeFullyDeletedCache = new Map<string, boolean>();
    const visiting = new Set<string>();

    function isSubtreeFullyDeleted(id: string): boolean {
      if (subtreeFullyDeletedCache.has(id)) {
        return Boolean(subtreeFullyDeletedCache.get(id));
      }

      // A parent_id cycle would recurse forever. Treat the re-entry as
      // "not deleted" so a cycle never prunes comments out of the list.
      if (visiting.has(id)) {
        return false;
      }

      const node = byId.get(id);
      if (!node) {
        subtreeFullyDeletedCache.set(id, true);
        return true;
      }

      const children = rawChildrenMap[id] || [];
      if (children.length === 0) {
        const leafDeleted = node.status === 'DELETED';
        subtreeFullyDeletedCache.set(id, leafDeleted);
        return leafDeleted;
      }

      visiting.add(id);
      let allChildrenDeleted = true;
      for (const childId of children) {
        if (!isSubtreeFullyDeleted(childId)) {
          allChildrenDeleted = false;
          break;
        }
      }
      visiting.delete(id);

      const result = node.status === 'DELETED' && allChildrenDeleted;
      subtreeFullyDeletedCache.set(id, result);
      return result;
    }

    // Second pass: build filtered map excluding children whose entire subtree is deleted
    const childrenMap: IdsMap = {};
    for (const [parentId, childIds] of Object.entries(rawChildrenMap)) {
      const kept = childIds.filter(
        (childId) => !isSubtreeFullyDeleted(childId),
      );
      if (kept.length) {
        childrenMap[parentId] = kept;
      }
    }

    return childrenMap;
  },
);

const getIdToCommentMap = lruMemoize(
  (comments: AssetsSegmentSchemaWithIdPatched[]) =>
    new Map<string, AssetsSegmentSchemaWithIdPatched>(
      comments.map((comment) => [comment.id, comment]),
    ),
);

const filterDeletedComments = lruMemoize(
  (
    allComments: AssetsSegmentSchemaWithIdPatched[],
    comments: AssetsSegmentSchemaWithIdPatched[],
  ) => {
    // Filter out deleted comments which don't have children
    const childrenMap = getChildrenMap(allComments);
    return comments.filter((comment) => {
      if (comment && comment.status === 'DELETED') {
        return childrenMap[comment.id];
      }

      return true;
    });
  },
);

const getComments = (comments: AssetsSegmentSchemaWithIdPatched[]) => comments;

export type CommentListFilters = {
  annotationFilter?: string[];
  completionFilter?: string[];
  replyFilter?: string[];
  mentionFilter?: string[];
  commenterFilter?: string[];
  colorFilter?: string[];
  textFilter?: string;
  versionFilter?: string;
  sort?: CommentSortExpression;
  visibilityFilter?: string[];
};

const getFilters = (
  _comments: AssetsSegmentSchemaWithIdPatched[],
  filters: CommentListFilters = {},
) => filters;

type SharedCommentFilters = {
  colorsFilter: string[];
  annotationFilter: string[];
  completionFilter: string[];
  mentionFilter: string[];
  commenterFilter: string[];
  visibilityFilter: string[];
  textFilter: string;
};

function toSharedFilters(filters: CommentListFilters): SharedCommentFilters {
  return {
    colorsFilter: filters.colorFilter ?? [],
    annotationFilter: filters.annotationFilter ?? [],
    completionFilter: filters.completionFilter ?? [],
    mentionFilter: filters.mentionFilter ?? [],
    commenterFilter: filters.commenterFilter ?? [],
    visibilityFilter: filters.visibilityFilter ?? [],
    textFilter: filters.textFilter ?? '',
  };
}

/**
 * Per-comment filter predicate shared by root comments and replies. A comment
 * passes when it satisfies every active filter; inactive filters are skipped.
 */
function commentPassesSharedFilters(
  comment: AssetsSegmentSchemaWithIdPatched,
  filters: SharedCommentFilters,
) {
  const {
    colorsFilter,
    annotationFilter,
    completionFilter,
    mentionFilter,
    commenterFilter,
    visibilityFilter,
    textFilter,
  } = filters;

  if (colorsFilter.length > 0) {
    if (
      !comment.segment_color ||
      !colorsFilter.includes(comment.segment_color)
    ) {
      return false;
    }
  }
  if (annotationFilter.length > 0) {
    const passes =
      (annotationFilter.includes('has_annotation') &&
        Boolean(comment.has_drawing)) ||
      (annotationFilter.includes('has_no_annotation') && !comment.has_drawing);
    if (!passes) {
      return false;
    }
  }
  if (completionFilter.length > 0) {
    const passes =
      (completionFilter.includes('completed') &&
        Boolean(comment.segment_checked)) ||
      (completionFilter.includes('not_completed') && !comment.segment_checked);
    if (!passes) {
      return false;
    }
  }
  if (mentionFilter.length > 0) {
    const hasMention = Boolean(comment.segment_text?.match(MENTION_REGEX));
    const passes =
      (mentionFilter.includes('has_mention') && hasMention) ||
      (mentionFilter.includes('no_mention') && !hasMention);
    if (!passes) {
      return false;
    }
  }
  if (commenterFilter.length > 0) {
    const passes =
      (comment.user_id !== undefined &&
        commenterFilter.includes(comment.user_id)) ||
      (comment.share_user_email != null &&
        commenterFilter.includes(comment.share_user_email));
    if (!passes) {
      return false;
    }
  }
  if (visibilityFilter.length > 0) {
    const passes =
      (visibilityFilter.includes('public') && !comment.is_internal) ||
      (visibilityFilter.includes('internal') && Boolean(comment.is_internal));
    if (!passes) {
      return false;
    }
  }
  if (textFilter) {
    const lowerTextFilter = textFilter.toLowerCase();
    if (
      !comment.segment_text ||
      !comment.segment_text.toLowerCase().includes(lowerTextFilter)
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Whether any of a comment's live replies pass the shared filters.
 * Uses an iterative DFS so it short-circuits on the first match without
 * allocating a flat array of all descendant ids. Deleted replies are skipped
 * so a removed reply's content can't surface its parent.
 */
function hasReplyPassingSharedFilters(
  comment: AssetsSegmentSchemaWithIdPatched,
  filters: SharedCommentFilters,
  childrenMap: IdsMap,
  idToCommentMap: Map<string, AssetsSegmentSchemaWithIdPatched>,
) {
  const stack = [...(childrenMap[comment.id] ?? [])];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined) {
      continue;
    }
    const reply = idToCommentMap.get(id);
    if (
      reply !== undefined &&
      reply.status !== 'DELETED' &&
      commentPassesSharedFilters(reply, filters)
    ) {
      return true;
    }
    const grandchildren = childrenMap[id];
    if (grandchildren) {
      stack.push(...grandchildren);
    }
  }
  return false;
}

export const selectFilteredRootComments = createSelector(
  [getComments, getFilters],
  (allComments, filters) => {
    const {
      replyFilter = [],
      versionFilter = 'all',
      sort = 'time_start_milliseconds,asc',
    } = filters;
    const sharedFilters = toSharedFilters(filters);
    const childrenMap = getChildrenMap(allComments);
    const idToCommentMap = getIdToCommentMap(allComments);

    let filteredRootComments = allComments.filter(
      (comment) => !comment.parent_id,
    );

    if (replyFilter.length > 0) {
      filteredRootComments = filteredRootComments.filter((comment) => {
        const hasReplies = Boolean(childrenMap[comment.id]?.length);
        return (
          (replyFilter.includes('has_replies') && hasReplies) ||
          (replyFilter.includes('no_replies') && !hasReplies)
        );
      });
    }

    // A root stays when it matches the filters, or when one of its replies does,
    // so filtering by a reply still surfaces its parent.
    filteredRootComments = filteredRootComments.filter(
      (comment) =>
        commentPassesSharedFilters(comment, sharedFilters) ||
        hasReplyPassingSharedFilters(
          comment,
          sharedFilters,
          childrenMap,
          idToCommentMap,
        ),
    );

    if (sort) {
      // biome-ignore lint/plugin: sort is built by the sort menu as "field,order", but split() only ever yields string[].
      const [field, sortOrder] = sort.split(',') as [
        keyof AssetsSegmentSchema,
        SortOrder,
      ];
      filteredRootComments = orderBy(
        filteredRootComments,
        [field],
        [sortOrder],
      );
    }

    if (versionFilter !== 'all') {
      filteredRootComments = filteredRootComments.filter(
        (comment) => comment.version_id === versionFilter,
      );
    }

    filteredRootComments = filterDeletedComments(
      allComments,
      filteredRootComments,
    );

    return filteredRootComments;
  },
);

export const selectCommentRepliesMap = createSelector(
  [getComments, selectFilteredRootComments, getFilters],
  (allComments, filteredRootComments, filters) => {
    const sharedFilters = toSharedFilters(filters);
    const idToCommentMap = getIdToCommentMap(allComments);

    const commentRepliesMap: Record<
      string,
      AssetsSegmentSchemaWithIdPatched[]
    > = {};

    const childrenMap = getChildrenMap(allComments);
    for (const comment of filteredRootComments) {
      const replies = getChildrenIds(comment.id, childrenMap)
        .map((id) => idToCommentMap.get(id))
        .filter(truthyPredicate)
        // A filtered thread shows only its matching replies. Deleted replies
        // that still have a thread are kept by filterDeletedComments below.
        .filter((reply) => commentPassesSharedFilters(reply, sharedFilters));

      commentRepliesMap[comment.id] = filterDeletedComments(
        allComments,
        replies,
      );
    }

    return commentRepliesMap;
  },
);

/**
 * Ids of root comments that the panel should auto-expand because they were kept
 * only by a reply match (they don't pass the filters themselves). Derived from
 * selectFilteredRootComments, so it inherits its root-level exclusions and
 * yields an empty list when no filters are active.
 */
export const selectRootIdsWithMatchingReply = createSelector(
  [selectFilteredRootComments, getFilters],
  (filteredRootComments, filters) => {
    const sharedFilters = toSharedFilters(filters);
    return filteredRootComments
      .filter((comment) => !commentPassesSharedFilters(comment, sharedFilters))
      .map((comment) => comment.id);
  },
);

/**
 * Total (unfiltered) reply count per filtered root comment. Used for the
 * "N Replies" button label and hasReplies flag so the expand button reflects
 * the full thread size rather than the number of currently-matching replies.
 */
export const selectTotalRepliesCountMap = createSelector(
  [getComments, selectFilteredRootComments],
  (allComments, filteredRootComments) => {
    const childrenMap = getChildrenMap(allComments);
    const result: Record<string, number> = {};
    for (const comment of filteredRootComments) {
      result[comment.id] = getChildrenIds(comment.id, childrenMap).length;
    }
    return result;
  },
);
